import * as React from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AccessibleSite } from '@/auth/useSelectedSite';

/**
 * Lightweight site picker — plain button + popover, no Radix. Renders in the
 * eagerly-loaded AppShell, so we keep the dep surface tiny.
 *
 * Click the trigger to expand a list of accessible sites; selecting one calls
 * `onSelect(slug)` (which writes localStorage and navigates the user to that
 * site's first enabled feature).
 */
export function SiteSwitcher({
  sites,
  selectedSlug,
  onSelect,
  variant = 'sidebar',
}: {
  sites: AccessibleSite[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
  variant?: 'sidebar' | 'sheet';
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  if (sites.length === 0) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground italic">
        No site assignments. Ask an admin to add you to a site.
      </div>
    );
  }

  const selected = sites.find((s) => s.slug === selectedSlug);

  const triggerClass =
    variant === 'sidebar'
      ? 'w-full bg-background/40 border-sidebar-border'
      : 'w-full bg-background border-input';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex items-center justify-between gap-2 rounded-md border px-3 py-2 hover-elevate transition-colors',
          triggerClass,
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 text-left">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-none mb-0.5">
              Current site
            </div>
            <div className="truncate text-sm font-medium">
              {selected?.name ?? 'Select a site'}
            </div>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          className={cn(
            'absolute z-50 mt-1 w-full rounded-md border bg-background shadow-lg overflow-hidden',
            variant === 'sidebar' ? 'border-sidebar-border' : 'border-input',
          )}
        >
          {sites.map((s) => {
            const isSelected = s.slug === selectedSlug;
            return (
              <li key={s.slug}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onSelect(s.slug);
                  }}
                  className={cn(
                    'w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover-elevate text-left',
                    isSelected && 'font-medium',
                  )}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="truncate">{s.name}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
